import {
  type JourneyContractSegment,
  type JourneyContractWay,
  DictionaryProvider,
  FootprintBadge,
  JourneyContractSegmentTransportType,
  JourneyContractWayTransportCategory,
} from '@lmn/lmn-components';
import styled, { css } from 'styled-components';
import { type HTMLAttributes, useState } from 'react';
import { type TranslationFunction, useTranslation } from '@/app/components/translations/Translation';
import {
  getSegmentArrivalCity,
  getSegmentDepartureCity,
} from '@/app/components/hotel-detail/flight-train/TransportCommon';
import { Text } from '@lmn/design-system';
import { useSearchParams } from 'next/navigation';
import { URLParameters } from '@/app/common/urlParameters';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { AirlineLogo } from '@/app/common/components/AirlineLogo';
import { Icon } from '@/app/common/icons/Icon';
import { changeDateFormat } from '../common/dates';
import TransportDetails, { adaptTransport } from './TransportDetails';
import { getCompanies, getCompaniesLogos } from './utils';
import { useIsLgOrGreater } from '../hooks/useMediaSSR';
import { keyUpHandler } from '../common/common.helper';

export const flightClassAvailabilityTranslations: { [key: string]: string } = {
  Y: 'vacation.journey.class.tourist',
  P: 'vacation.journey.class.touristpremium',
  C: 'vacation.journey.class.business',
  F: 'vacation.journey.class.firstclass',
};

const trainClassAvailabilityTranslations: { [key: string]: { [key: string]: string } } = {
  eurostar: {
    c: 'vacation.journey.9f.c',
    p: 'vacation.journey.9f.p',
    y: 'vacation.journey.9f.y',
  },
  deutscheBahn: {
    c: 'vacation.journey.2a.c',
    y: 'vacation.journey.2a.y',
  },
  sncf: {
    c: 'vacation.journey.2c.c',
    p: 'vacation.journey.2c.p',
    y: 'vacation.journey.2c.y',
  },
  fsi: {
    b: 'vacation.journey.fsi.b',
    e: 'vacation.journey.fsi.e',
    p: 'vacation.journey.fsi.p',
    s: 'vacation.journey.fsi.s',
    y: 'vacation.journey.fsi.y',
  },
  itl: {
    c: 'vacation.journey.itl.c',
    p: 'vacation.journey.itl.p',
    s: 'vacation.journey.itl.s',
    t: 'vacation.journey.itl.t',
  },
  rnf: {
    c: 'vacation.journey.rnf.c',
    f: 'vacation.journey.rnf.f',
    p: 'vacation.journey.rnf.p',
    t: 'vacation.journey.rnf.t',
    u: 'vacation.journey.rnf.u',
    y: 'vacation.journey.rnf.y',
  },
};

export function adaptWay(way: TransportBoundType, t: TranslationFunction): JourneyContractWay {
  const originCityName = getSegmentDepartureCity(way.segments[0]);
  const destinationCityName = getSegmentArrivalCity(way.segments[way.segments.length - 1]);

  return {
    onSelected: () => null,
    baggage: {
      hold: way.includeBaggage,
      hand: way.handBaggageIncluded,
    },
    durationInMinutes: way.timeIntervalInMinutes,
    seats: way.availableSeats && way.availableSeats > 0 && way.availableSeats < 9 ? way.availableSeats : undefined,
    transportCategory: JourneyContractWayTransportCategory.STANDARD,
    segments: way.segments.map((it, index) => ({
      ...adaptSegment(it, t),
      key: it.transportNumber || index,
    })),
    selected: false,
    locations: {
      origin: originCityName,
      destination: destinationCityName,
    },
  };
}

function adaptSegment(segment: TransportSegment, t: TranslationFunction): JourneyContractSegment {
  const technicalStopsField =
    segment.technicalStops && segment.technicalStops.constructor === Array ? segment.technicalStops : [];

  const classKey = segment.classAvailability
    ? segment.type === 'FLIGHT'
      ? flightClassAvailabilityTranslations[segment.classAvailability || 'Y']
      : trainClassAvailabilityTranslations[segment.company.code.toLowerCase()]?.[
          segment.classAvailability.toLowerCase()
        ]
    : null;

  return {
    transport: {
      class: classKey ? t(classKey) : t('vacation.journey.class.tourist'),
      type:
        segment.type === 'TRAIN'
          ? JourneyContractSegmentTransportType.TRAIN
          : segment.type === 'BUS'
            ? JourneyContractSegmentTransportType.BUS
            : JourneyContractSegmentTransportType.FLIGHT,
      companyCode: segment.company.code,
      companyName: segment.company.name,
      operativeId: segment.company.code + segment.transportNumber,
      operateBy:
        segment.operator && segment.operator.name && segment.operator.name !== segment.company.name
          ? { code: segment.operator.code, name: segment.operator.name }
          : undefined,
    },
    arrival: {
      hub: {
        id: segment.arrivalHub.id || '',
        name: segment.arrivalHub.name,
        terminal: segment.arrivalHub.terminal,
      },
      datetime: formatDateTime(segment.arrivalDate),
    },
    departure: {
      hub: {
        id: segment.departureHub.id || '',
        name: segment.departureHub.name,
        terminal: segment.departureHub.terminal,
      },
      datetime: formatDateTime(segment.departureDate),
    },
    technicalStops: technicalStopsField.length
      ? technicalStopsField.map((stop) => ({
          arrivalTime: formatDateTime(stop.startDate),
          departureTime: formatDateTime(stop.endDate),
          hub: {
            id: stop.locationCode,
            name: stop.locationName,
            terminal: stop.terminal || undefined,
          },
        }))
      : undefined,
  };
}

function formatDateTime(date: string) {
  return changeDateFormat(date, `dd/MM/yy'#'HH:mm`, `yyyy-MM-dd'T'HH:mm:ss.SSS`);
}

const TransportCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 16px 0px 16px;
`;

export const FootprintContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  white-space: nowrap;

  & > div {
    padding: 2px 6px;
  }
`;

export const FootprintBadgeContainer = styled.div<{ $desktop: boolean }>`
  display: flex;
  align-items: center;
  ${({ $desktop }) =>
    $desktop
      ? css`
          background-color: ${({ theme }) => theme.commons.colors.gray50};
        `
      : undefined};

  border-radius: 4px;

  > div {
    border: 0px;
    background-color: transparent;
  }
`;

const AirlinesContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AirlineLogos = styled.div`
  display: flex;
  gap: 2px;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  height: 20px;
  width: 20px;
  background-color: ${({ theme }) => theme.commons.colors.gray50};
`;

const StyledAirlineLogo = styled(AirlineLogo)`
  border-radius: 4px;
`;

export type TransportJourneySummaryProps = {
  inbound: TransportBoundType;
  outbound: TransportBoundType;
  co2Footprint?: CO2Footprint;
  transportId: string;
  openDetailsClicked: () => void;
  onCo2InfoClick?: () => void;
  numberOfBaggagePiecesIncluded?: number;
} & HTMLAttributes<HTMLDivElement>;

export default function TransportJourneySummary(props: TransportJourneySummaryProps) {
  const {
    inbound,
    outbound,
    transportId,
    co2Footprint,
    openDetailsClicked,
    onCo2InfoClick,
    numberOfBaggagePiecesIncluded,
    ...rest
  } = props;
  const { localization } = usePageWrapperData();
  const { t, tripCardDictionary } = useTranslation();
  const [expand, setExpand] = useState<boolean>(false);
  const showCo2 = co2Footprint && co2Footprint.co2kg > 0 && !!onCo2InfoClick;
  const segments = [...outbound.segments, ...inbound.segments];
  const logos = getCompaniesLogos(segments);
  const companies = getCompanies(t, segments);
  // Operators with no logo of their own fall back to an icon, so keep the kind of
  // transport each of them runs: a train operator gets a train, not a plane.
  const transportTypeByCompany = new Map(segments.map((segment) => [segment.company.code, segment.type]));
  const isMobile = !useIsLgOrGreater();
  const searchParams = useSearchParams();
  const debug = !!searchParams.get(URLParameters.DEBUG);

  return (
    <div {...rest}>
      <DictionaryProvider labels={tripCardDictionary}>
        {isMobile ? (
          <TransportCardHeader onClick={openDetailsClicked}>
            <AirlinesContainer>
              <AirlineLogos>
                {logos.map((logo) => (
                  <LogoContainer key={`container-company-${logo}`}>
                    {/* No name: the carrier names are spelled out in the text beside these
                        chips, and `logo` is the IATA code, which a screen reader would only
                        read back as letters. */}
                    <StyledAirlineLogo
                      code={logo}
                      size={20}
                      iconSize={20}
                      transportType={transportTypeByCompany.get(logo)}
                    />
                  </LogoContainer>
                ))}
              </AirlineLogos>
              <Text variant="BodySmall">{companies}</Text>
            </AirlinesContainer>
            {showCo2 ? (
              <FootprintContainer
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onCo2InfoClick();
                }}
                onKeyUp={keyUpHandler(onCo2InfoClick)}
              >
                <FootprintBadgeContainer $desktop={!isMobile}>
                  <FootprintBadge footprint={co2Footprint} useShortVariant={true} locale={localization.locale} />
                  <Icon category="Semantics" name="icon-circle-info" size={16} color="iconPrimary" alt="" />
                </FootprintBadgeContainer>
              </FootprintContainer>
            ) : null}
          </TransportCardHeader>
        ) : null}
        <TransportDetails
          key={`${transportId}-flight-details`}
          dictionary={tripCardDictionary}
          isWaitingState={false}
          transportData={adaptTransport({
            transportId,
            inbound,
            outbound,
          })}
          handleTransportClicked={openDetailsClicked}
          index={0}
          expand={!!expand}
          setExpand={setExpand}
          itemsLength={1}
          showDebug={debug}
          dealsCount={1}
          numberOfBaggagePiecesIncluded={numberOfBaggagePiecesIncluded}
        />
      </DictionaryProvider>
    </div>
  );
}
